import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Add, CheckCircleOutline, Edit } from '@mui/icons-material';
import { Formik, FormikHelpers } from 'formik';
import { Timestamp } from 'firebase/firestore';
import {
  activateCheckInDate,
  createCheckInDate,
  updateCheckInDate,
  useActor,
} from '../../lib/audited-writes';
import { useFirebaseContext } from '../../lib/firebase-context';
import { useToast } from '../../lib/toast-context';
import { CheckinDate } from '../../lib/types';
import { localDateKey, parseLocalNoon } from '../../lib/dates';
import {
  CheckInDateStats,
  buildSchema,
} from './check-in-dates-list.utils';

type CheckInDateFormValues = {
  // Native <input type="date"> value: 'yyyy-mm-dd'.
  date: string;
};

const blankFormValues: CheckInDateFormValues = { date: '' };

const CheckInDateFormDialog = ({
  open,
  checkInDate,
  existingDates,
  activeEventRef,
  onClose,
  onSaved,
  onError,
}: {
  open: boolean;
  // null = add mode; CheckinDate = edit mode
  checkInDate: CheckinDate | null;
  existingDates: CheckinDate[];
  activeEventRef: string | undefined;
  onClose: () => void;
  onSaved: (mode: 'add' | 'edit') => void;
  onError: (msg: string) => void;
}) => {
  const actor = useActor();
  const isEdit = checkInDate !== null;
  const [isWorking, setIsWorking] = useState(false);

  const handleClose = () => {
    if (isWorking) return;
    onClose();
  };

  const validationSchema = useMemo(
    () => buildSchema(existingDates, checkInDate?.ref),
    [existingDates, checkInDate?.ref],
  );

  const initialValues = useMemo<CheckInDateFormValues>(
    () =>
      checkInDate
        ? { date: localDateKey(checkInDate.date) }
        : blankFormValues,
    [checkInDate],
  );

  // Count of check-ins on this date — used to surface a warning that
  // editing the date re-attributes them. We filter against the live
  // attendee set so refs left behind by soft-deletes (or VBS cancellations)
  // don't inflate the warning count past what the volunteer can see.
  const { attendees } = useFirebaseContext();
  const liveAttendeeRefs = useMemo(
    () => new Set(attendees.map((a) => a.ref)),
    [attendees],
  );
  const checkedInCount =
    (checkInDate?.checkedInRefs ?? []).filter((r) => liveAttendeeRefs.has(r))
      .length;

  const handleSubmit = async (
    values: CheckInDateFormValues,
    { setSubmitting, resetForm }: FormikHelpers<CheckInDateFormValues>,
  ) => {
    setIsWorking(true);
    let succeeded = false;
    try {
      const parsed = parseLocalNoon(values.date);
      if (!parsed) {
        onError('Invalid date');
        return;
      }
      if (checkInDate) {
        // Mid-session safety: refuse to apply edits if the admin flipped the
        // active event while this dialog was open — the check-in-date doc
        // would no longer belong to the event the user is viewing.
        if (!activeEventRef || checkInDate.eventRef !== activeEventRef) {
          onError('Active event changed — close and reopen this date');
          return;
        }
        // Edit: only the date field. isActive / checkedInRefs / checkedOutRefs
        // / eventRef are preserved by omission so all existing check-ins stay
        // attached to this doc.
        await updateCheckInDate(actor, checkInDate, {
          date: Timestamp.fromDate(parsed),
        });
      } else {
        if (!activeEventRef) {
          onError('No active event — set one in Admin first');
          return;
        }
        await createCheckInDate(actor, {
          eventRef: activeEventRef,
          date: Timestamp.fromDate(parsed),
          isActive: false,
          checkedInRefs: [],
          checkedOutRefs: [],
        });
      }
      succeeded = true;
    } catch (error) {
      console.error('Error saving check-in date', error);
      onError(
        isEdit ? 'Failed to update check-in date' : 'Failed to add check-in date',
      );
    } finally {
      setSubmitting(false);
      setIsWorking(false);
    }
    if (succeeded) {
      if (!isEdit) resetForm();
      onSaved(isEdit ? 'edit' : 'add');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        {isEdit ? 'Edit check-in date' : 'Add check-in date'}
      </DialogTitle>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <DialogContent>
              <Stack spacing={2}>
                {isEdit && checkedInCount > 0 && (
                  <Alert severity='warning'>
                    {checkedInCount} check-in{checkedInCount === 1 ? '' : 's'}{' '}
                    will be re-attributed to the new date. Existing check-in
                    history for this day will move with it.
                  </Alert>
                )}
                <TextField
                  name='date'
                  label='Date'
                  type='date'
                  value={values.date}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.date && Boolean(errors.date)}
                  helperText={touched.date && (errors.date as string)}
                  disabled={isSubmitting}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  // The native <input type="date"> calendar picker icon
                  // is rendered by the browser and isn't theme-aware, so on
                  // dark mode it shows up dark-on-dark. Inverting the icon
                  // turns it white to match surrounding chrome.
                  sx={{
                    '& input::-webkit-calendar-picker-indicator': {
                      filter: 'invert(1)',
                      cursor: 'pointer',
                    },
                  }}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                type='submit'
                variant='contained'
                disabled={isSubmitting}
              >
                {isEdit ? 'Save' : 'Add'}
              </Button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

const CheckInDatesList = () => {
  const actor = useActor();
  // Tracks the date ref currently being toggled. Used to disable all
  // Activate/Deactivate buttons while a batched write is in flight, which
  // also prevents two activate clicks from leaving two `isActive: true`
  // rows. `null` means no toggle in progress.
  const [isToggling, setIsToggling] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<CheckinDate | null>(null);
  const { checkInDates, activeEvent } = useFirebaseContext();
  const toast = useToast();

  // Mutual-exclusion-aware toggle: when activating a date, atomically demote
  // every other active date in the same event in the same batch so only one
  // `isActive: true` ever exists. Mirrors `handleActivate` for events in
  // manage-events.tsx.
  const toggleActive = async (target: CheckinDate) => {
    if (isToggling) return;
    setIsToggling(target.ref);
    try {
      await activateCheckInDate(actor, target, checkInDates ?? []);
    } catch (error) {
      console.error('Error updating check-in date', error);
      toast.error('Failed to update check-in date');
    } finally {
      setIsToggling(null);
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant='contained'
          startIcon={<Add />}
          onClick={() => setAdding(true)}
        >
          Add check-in date
        </Button>
      </Box>

      {checkInDates && (
        <List dense disablePadding>
          {checkInDates.map((checkInDate) => (
            <ListItem
              key={checkInDate.ref}
              divider
              sx={{
                py: 0.25,
                px: 1,
                gap: 1,
                '&:last-child': { borderBottom: 0 },
              }}
            >
              <Box
                sx={{
                  width: 24,
                  display: 'flex',
                  alignItems: 'center',
                  flexShrink: 0,
                }}
              >
                {checkInDate.isActive && (
                  <CheckCircleOutline color='success' fontSize='small' />
                )}
              </Box>
              <Typography
                variant='body2'
                sx={{ minWidth: 140, flexShrink: 0, fontWeight: 500 }}
              >
                {checkInDate.date.toDate().toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </Typography>
              <CheckInDateStats date={checkInDate} />
              <Button
                disabled={Boolean(isToggling)}
                size='small'
                color={checkInDate.isActive ? 'warning' : 'success'}
                onClick={() => toggleActive(checkInDate)}
                sx={{ minWidth: 96 }}
              >
                {isToggling === checkInDate.ref
                  ? checkInDate.isActive
                    ? 'Deactivating…'
                    : 'Activating…'
                  : checkInDate.isActive
                  ? 'Deactivate'
                  : 'Activate'}
              </Button>
              <IconButton
                size='small'
                aria-label={`Edit ${checkInDate.date
                  .toDate()
                  .toLocaleDateString('en-US')}`}
                onClick={() => setEditing(checkInDate)}
              >
                <Edit fontSize='small' />
              </IconButton>
            </ListItem>
          ))}
        </List>
      )}

      <CheckInDateFormDialog
        open={adding || editing !== null}
        checkInDate={editing}
        existingDates={checkInDates || []}
        activeEventRef={activeEvent?.ref}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={(mode) =>
          toast.success(
            mode === 'add' ? 'Check-in date added' : 'Check-in date saved',
          )
        }
        onError={(message) => toast.error(message)}
      />
    </>
  );
};

export default CheckInDatesList;
